<script setup>
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n, I18nT } from 'vue-i18n';
import Twilio from './Twilio.vue';
import ThreeSixtyDialogWhatsapp from './360DialogWhatsapp.vue';
import CloudWhatsapp from './CloudWhatsapp.vue';
import WhatsappEmbeddedSignup from './WhatsappEmbeddedSignup.vue';
// FORK_PATCH_BEGIN: channel-hosting-provider
import ChannelHostingWhatsapp from './ChannelHostingWhatsapp.vue';
// FORK_PATCH_END: channel-hosting-provider
// FORK_PATCH_BEGIN: REQ-20260914-02
import YcloudWhatsapp from 'custom/dashboard/extensions/ycloud_whatsapp/YcloudWhatsapp.vue';
// FORK_PATCH_END: REQ-20260914-02
import ChannelSelector from 'dashboard/components/ChannelSelector.vue';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const PROVIDER_TYPES = {
  WHATSAPP: 'whatsapp',
  TWILIO: 'twilio',
  WHATSAPP_CLOUD: 'whatsapp_cloud',
  WHATSAPP_EMBEDDED: 'whatsapp_embedded',
  WHATSAPP_MANUAL: 'whatsapp_manual',
  THREE_SIXTY_DIALOG: '360dialog',
  // FORK_PATCH_BEGIN: channel-hosting-provider-key
  CHANNEL_HOSTING: 'channel_hosting',
  // FORK_PATCH_END: channel-hosting-provider-key
  // FORK_PATCH_BEGIN: REQ-20260914-02
  YCLOUD: 'ycloud',
  // FORK_PATCH_END: REQ-20260914-02
};

const hasWhatsappAppId = computed(() => {
  return (
    window.chatwootConfig?.whatsappAppId &&
    window.chatwootConfig.whatsappAppId !== 'none'
  );
});

const selectedProvider = computed(() => route.query.provider);

const showProviderSelection = computed(() => !selectedProvider.value);

const showConfiguration = computed(() => Boolean(selectedProvider.value));

const availableProviders = computed(() => [
  {
    key: PROVIDER_TYPES.WHATSAPP,
    title: t('INBOX_MGMT.ADD.WHATSAPP.PROVIDERS.WHATSAPP_CLOUD'),
    description: t('INBOX_MGMT.ADD.WHATSAPP.PROVIDERS.WHATSAPP_CLOUD_DESC'),
    icon: 'i-woot-whatsapp',
  },
  {
    key: PROVIDER_TYPES.TWILIO,
    title: t('INBOX_MGMT.ADD.WHATSAPP.PROVIDERS.TWILIO'),
    description: t('INBOX_MGMT.ADD.WHATSAPP.PROVIDERS.TWILIO_DESC'),
    icon: 'i-woot-twilio',
  },
  // FORK_PATCH_BEGIN: channel-hosting-provider-card
  {
    key: PROVIDER_TYPES.CHANNEL_HOSTING,
    title: t('INBOX_MGMT.ADD.WHATSAPP.PROVIDERS.HOSTING'),
    description: t('INBOX_MGMT.ADD.WHATSAPP.PROVIDERS.HOSTING_DESC'),
    icon: 'i-woot-whatsapp',
  },
  // FORK_PATCH_END: channel-hosting-provider-card
  // FORK_PATCH_BEGIN: REQ-20260914-02
  {
    key: PROVIDER_TYPES.YCLOUD,
    title: t('INBOX_MGMT.ADD.WHATSAPP.PROVIDERS.YCLOUD'),
    description: t('INBOX_MGMT.ADD.WHATSAPP.PROVIDERS.YCLOUD_DESC'),
    icon: 'i-woot-whatsapp',
  },
  // FORK_PATCH_END: REQ-20260914-02
]);

const selectProvider = providerValue => {
  router.push({
    name: route.name,
    params: route.params,
    query: { provider: providerValue },
  });
};

const shouldShowCloudWhatsapp = provider => {
  return (
    provider === PROVIDER_TYPES.WHATSAPP_MANUAL ||
    (provider === PROVIDER_TYPES.WHATSAPP && !hasWhatsappAppId.value)
  );
};

const handleManualLinkClick = () => {
  selectProvider(PROVIDER_TYPES.WHATSAPP_MANUAL);
};
</script>

<template>
  <div class="overflow-auto col-span-6 p-6 w-full h-full">
    <div v-if="showProviderSelection">
      <div class="mb-10 text-left">
        <h1 class="mb-2 text-lg font-medium text-n-slate-12">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.SELECT_PROVIDER.TITLE') }}
        </h1>
        <p class="text-sm leading-relaxed text-n-slate-11">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.SELECT_PROVIDER.DESCRIPTION') }}
        </p>
      </div>

      <div class="flex gap-6 justify-start flex-wrap">
        <ChannelSelector
          v-for="provider in availableProviders"
          :key="provider.key"
          :title="provider.title"
          :description="provider.description"
          :icon="provider.icon"
          @click="selectProvider(provider.key)"
        />
      </div>
    </div>

    <div v-else-if="showConfiguration">
      <!-- FORK_PATCH_BEGIN: channel-hosting-wizard -->
      <ChannelHostingWhatsapp
        v-if="selectedProvider === PROVIDER_TYPES.CHANNEL_HOSTING"
      />
      <!-- FORK_PATCH_END: channel-hosting-wizard -->
      <!-- FORK_PATCH_BEGIN: REQ-20260914-02 -->
      <YcloudWhatsapp
        v-else-if="selectedProvider === PROVIDER_TYPES.YCLOUD"
        class="px-6 py-5 rounded-2xl border border-n-weak"
      />
      <!-- FORK_PATCH_END: REQ-20260914-02 -->
      <div
        v-else
        class="px-6 py-5 rounded-2xl border border-n-weak"
      >
        <!-- Show embedded signup if app ID is configured -->
        <div
          v-if="
            hasWhatsappAppId && selectedProvider === PROVIDER_TYPES.WHATSAPP
          "
        >
          <WhatsappEmbeddedSignup />

          <!-- Manual setup fallback option -->
          <div class="pt-6 mt-6 border-t border-n-weak">
            <I18nT
              keypath="INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.MANUAL_FALLBACK"
              tag="p"
              class="text-sm text-n-slate-11"
            >
              <template #link>
                <a
                  href="#"
                  class="underline text-n-brand"
                  @click.prevent="handleManualLinkClick"
                >
                  {{
                    $t(
                      'INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.MANUAL_LINK_TEXT'
                    )
                  }}
                </a>
              </template>
            </I18nT>
          </div>
        </div>

        <!-- Show manual setup -->
        <CloudWhatsapp v-else-if="shouldShowCloudWhatsapp(selectedProvider)" />

        <!-- Other providers -->
        <Twilio
          v-else-if="selectedProvider === PROVIDER_TYPES.TWILIO"
          type="whatsapp"
        />
        <ThreeSixtyDialogWhatsapp
          v-else-if="selectedProvider === PROVIDER_TYPES.THREE_SIXTY_DIALOG"
        />
        <CloudWhatsapp v-else />
      </div>
    </div>
  </div>
</template>
